import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useEffect, useState } from "react";
import {
    User, Bell, LogOut
} from "lucide-react";
import logo from "../assets/AiWisedom-logo.avif";
import LanguageDropdown from "./LanguageDropdown";
import { useTranslation } from "react-i18next";

const Navbar = () => {
    const { t } = useTranslation();
    const { user, isAuthenticated, logout } = useAuth();
    const navigate = useNavigate();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);


    const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);

    const handleLogout = () => {
        logout();
        navigate('/');
    };
    const toggleNotification = () => {
        setIsNotificationOpen(prev => !prev);
        setIsDropdownOpen(false);
    };
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest(".profile-dropdown")) {
                setIsDropdownOpen(false);
            }
            if (!target.closest(".notification-dropdown")) {
                setIsNotificationOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <header className="bg-white shadow-sm border-b border-gray-200 relative z-50">
            <div className="max-w-[83rem] mx-auto px-4 sm:px-[0rem] lg:px-[0rem]">
                <div className="flex items-center justify-between h-16">
                    {/* Logo and Navigation */}
                    <div className="flex items-center space-x-8">
                        <Link to="/" className="flex items-center space-x-3 cursor-pointer">
                            <img
                                src={logo}
                                alt="Resume Analyzer Logo"
                                // className="h-10 w-10 rounded-full shadow-md"
                                className="h-14 w-auto object-contain"
                            />
                        </Link>
                    </div>

                    {/* Search and Actions */}
                    <div className="flex items-center space-x-4">
                        <LanguageDropdown />
                        {/* Pricing Link */}
                        <Link to="/" className="text-primary hover:text-secondary font-medium transition-colors">
                            {t("navbar.home")}
                        </Link>
                        {/* <Link to="/plan" className="text-primary hover:text-secondary font-medium transition-colors">
                            Pricing
                        </Link> */}

                        {!isAuthenticated ? (
                            // Show when user is not logged in
                            <>
                                <Link to="/login" className="text-primary hover:text-secondary font-medium transition-colors">
                                    {t("navbar.login")}
                                </Link>
                                <Link
                                    to="/register"
                                    className="bg-primary text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-800 hover:text-secondary transition-colors shadow-sm"
                                >
                                    {t("navbar.register")}
                                </Link>
                            </>
                        ) : (
                            // Show when user is logged in
                            <>
                                {/* Notifications */}
                                <div className="relative notification-dropdown">
                                    {/* <button
                                        onClick={toggleNotification}
                                        className="relative p-2 text-gray-500 hover:text-primary transition-colors focus:outline-none"
                                    >
                                        <Bell className="w-5 h-5" />
                                        <span className="absolute -top-1 -right-1 bg-secondary text-primary text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                                            10
                                        </span>
                                    </button> */}

                                    {/* Notification Dropdown */}
                                    {isNotificationOpen && (
                                        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 z-50">
                                            <div className="px-4 py-3 border-b border-gray-100">
                                                <p className="text-sm font-semibold text-primary">
                                                    Notifications
                                                </p>
                                            </div>

                                            <ul className="max-h-60 overflow-y-auto">
                                                <li className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                    🔔 New job created
                                                </li>
                                                <li className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                    📄 Job status updated
                                                </li>
                                                <li className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                    👤 New user registered
                                                </li>
                                            </ul>

                                            <div className="px-4 py-2 border-t border-gray-100 text-center">
                                                <Link
                                                    to="/notifications"
                                                    className="text-sm text-primary hover:underline"
                                                >
                                                    View all notifications
                                                </Link>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Profile and Logout */}
                                <div className="flex items-center space-x-3">
                                    <div className="relative profile-dropdown">
                                        <div className="relative">
                                            {/* Avatar button */}
                                            <button
                                                onClick={toggleDropdown}
                                                className="w-[3rem] h-[3rem] bg-primary/10 rounded-full flex items-center justify-center focus:outline-none hover:bg-primary/20 transition-colors"
                                            >
                                                <span className="text-primary text-lg font-bold">
                                                    {user?.name ? user.name.charAt(0).toUpperCase() : <User className="w-4 h-4 text-primary" />}
                                                </span>
                                            </button>

                                            {/* Dropdown menu */}
                                            {isDropdownOpen && (
                                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-100 z-50">
                                                    <div className="px-4 py-3 border-b border-gray-100">
                                                        <p className="text-sm font-medium text-primary">{user?.name}</p>
                                                        <p className="text-xs text-gray-500">{user?.email}</p>
                                                    </div>
                                                    <ul className="py-1">
                                                        <li>
                                                            <Link
                                                                to="/profile"
                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary flex items-center gap-2 transition-colors"
                                                            >
                                                                <User className="w-4 h-4" />
                                                                {t("common.profile")}
                                                            </Link>
                                                        </li>
                                                        <li>
                                                            <button
                                                                onClick={handleLogout}
                                                                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-red-600 flex items-center gap-2 transition-colors"
                                                            >
                                                                <LogOut className="w-4 h-4" />
                                                                {t("common.logout")}
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
